<template>
  <div class="mt-2">
    <b-overlay :show="loading" rounded="sm" no-fade>
      <b-card>
        <b-row>
          <b-col xl="12" md="12" sm="12" cols="12">
            <b-row>
              <b-col xl="3" md="12" sm="12">
                <b-form-group>
                  <v-select
                    id="event"
                    v-model="selectedEvent"
                    :options="eventTypes"
                    :reduce="(val) => val"
                    :filterable="true"
                    @option:selected="selectedEvents"
                    label="title"
                    :clearable="false"
                    input-id="event"
                  />
                </b-form-group>
              </b-col>

              <b-col xl="3" md="12" sm="12">
                <b-form-group>
                  <v-select
                    id="status"
                    v-model="selectedStatus"
                    :options="statusTypes"
                    :reduce="(val) => val"
                    :filterable="true"
                    @option:selected="filterStatus"
                    label="title"
                    :clearable="false"
                    input-id="status"
                  />
                </b-form-group>
              </b-col>

              <b-col xl="3" md="12" sm="12">
                <b-form-group class="mb-40">
                  <b-input-group>
                    <b-form-input
                      id="filter-input"
                      v-model="filter"
                      type="search"
                      placeholder="Search..."
                    ></b-form-input>
                  </b-input-group>
                </b-form-group>
              </b-col>

              <b-col xl="3" md="12" sm="12">
                <b-form-group>
                  <b-input-group>
                    <flat-pickr
                      placeholder="Date Range"
                      v-model="rangeDate"
                      class="form-control"
                      :config="{ mode: 'range', dateFormat: 'm/d/Y' }"
                    />
                    <b-input-group-append>
                      <b-button @click="clearDate()" variant="warning"
                        >Clear</b-button
                      >
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>

            <b-table
              striped
              hover
              ref="refUserListTable"
              class="position-relative"
              :items="listReviewsData"
              :filter="filterQuery"
              :filter-included-fields="filterOn"
              filter-debounce="100"
              selectable
              select-mode="single"
              responsive
              :small="false"
              :fields="inHeaders"
              show-empty
              empty-text="No matching records found"
              @row-selected="onRowSelected"
            >
              <!-- Column: Id -->
              <template #cell(id)="data">
                <strong class="text-primary">#{{ data.item.id }}</strong>
              </template>

              <template #cell(firstName)="data">
                <span class="font-weight-bold">
                  {{ data.item.firstName ? data.item.firstName : "N/A" }}
                </span>
              </template>

              <template #cell(lastName)="data">
                <span class="font-weight-bold">
                  {{ data.item.lastName ? data.item.lastName : "N/A" }}
                </span>
              </template>

              <template #cell(observer)="data">
                <span class="font-weight-bold">
                  {{ data.item.observer ? data.item.observer : "N/A" }}
                </span>
              </template>

              <template #cell(event)="data">
                <span class="font-weight-bold">
                  {{ data.item.event ? data.item.event.title : "N/A" }}
                </span>
              </template>

              <template #cell(status)="data">
                <span class="font-weight-bold">
                  <strong
                    :class="
                      data.item.status === 'C' ? 'text-success' : 'text-warning'
                    "
                  >
                    <feather-icon
                      size="18"
                      v-b-tooltip.hover
                      :title="
                        data.item.status === 'C'
                          ? 'This quality review has been completed'
                          : 'This quality review is still in progress'
                      "
                      :icon="
                        data.item.status === 'C'
                          ? 'CheckIcon'
                          : 'AlertTriangleIcon'
                      "
                      :class="
                        data.item.status !== 'C'
                          ? 'animate__animated animate__heartBeat'
                          : ''
                      "
                    />
                  </strong>
                </span>
              </template>

              <template #cell(surveyDate)="data">
                <span class="font-weight-bold">
                  {{
                    data.item.surveyDate
                      ? dateFormat(data.item.surveyDate)
                      : "N/A"
                  }}
                </span>
              </template>
            </b-table>

            <div v-if="totalItems > 0" class="text-center mt-2">
              <small class="text-muted">{{ totalItems }} review(s) found</small>
            </div>
          </b-col>
        </b-row>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BTable,
  BDropdown,
  BDropdownItem,
  BOverlay,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import moment from "moment";
import { heightTransition } from "@core/mixins/ui/transition";
import store from "@/store";
import axiosIns from "@/libs/axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import reviewStoreModule from "@/views/reviews/reviewStoreModule";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BOverlay,
    vSelect,
    flatPickr,
    Treeselect,
  },
  mixins: [heightTransition],
  props: {
    userId: {
      type: [String, Number],
      required: true,
    },
  },
  setup() {
    const SURVEY_APP_STORE_MODULE_NAME = "review";

    // Register module
    if (!store.hasModule(SURVEY_APP_STORE_MODULE_NAME))
      store.registerModule(SURVEY_APP_STORE_MODULE_NAME, reviewStoreModule);
  },
  data() {
    return {
      loading: false,
      listReviewsData: [],
      filter: "",
      filterOn: [],
      inHeaders: [
        { key: "id", label: "ID", sortable: true },
        { key: "firstName", label: "First Name", sortable: true },
        { key: "lastName", label: "Last Name", sortable: true },
        { key: "observer", label: "Reviewer", sortable: true },
        { key: "event", label: "Event", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "surveyDate", label: "Review Date", sortable: true },
      ],
      eventTypes: [{ id: 0, title: "All Questionnaire", value: null }],
      statusTypes: [
        // `result.status` only ever holds 'C' (completed) or 'A' (in progress).
        // "Uncompleted" used to send 'U', which matched nothing.
        { id: 0, title: "All Status", value: null },
        { id: 1, title: "Completed", value: "C" },
        { id: 2, title: "In Progress", value: "A" },
      ],
      selectedEvent: { id: 0, title: "All Questionnaire", value: null },
      selectedStatus: { id: 0, title: "All Status", value: null },
      rangeDate: null,
      totalItems: 0,
    };
  },
  computed: {
    filterQuery() {
      let query = this.filter;
      if (this.rangeDate && this.rangeDate.length === 2) {
        const startDate = new Date(this.rangeDate[0]);
        const endDate = new Date(this.rangeDate[1]);
        return (item) => {
          const itemDate = new Date(item.surveyDate);
          const matchesSearch = this.filter
            ? Object.keys(item).some((key) =>
                String(item[key])
                  .toLowerCase()
                  .includes(this.filter.toLowerCase())
              )
            : true;
          const matchesDate = itemDate >= startDate && itemDate <= endDate;
          return matchesSearch && matchesDate;
        };
      }
      return query;
    },
  },
  mounted() {
    this.fetchReviews();
    this.fetchEvents();
  },
  methods: {
    fetchReviews() {
      this.loading = true;

      const filterData = {
        observer: null,
        event: this.selectedEvent ? this.selectedEvent : null,
        status: this.selectedStatus ? this.selectedStatus.value : null,
        filterDate: {
          startDate: null,
          endDate: null,
        },
        archive: "N", // Archive edilmemiş kayıtları göster
        userId: this.userId, // Kullanıcı ID'si filtrelemek için
      };

      if (this.rangeDate && this.rangeDate.length === 2) {
        filterData.filterDate.startDate = this.rangeDate[0];
        filterData.filterDate.endDate = this.rangeDate[1];
      }

      store
        .dispatch("review/getAllReviewResults", filterData)
        .then((response) => {
          // Backend'den gelen data'yı direkt kullan
          this.listReviewsData = response.data || [];
          this.totalItems = this.listReviewsData.length;
        })
        .catch((error) => {
          console.error("Error fetching reviews:", error);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              icon: "AlertTriangleIcon",
              text: "Error fetching reviews",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchEvents() {
      axiosIns
        .get("getQuestionnaires")
        .then((response) => {
          this.eventTypes = [
            { id: 0, title: "All Questionnaire", value: null },
            ...response.data.map((event) => ({
              title: event.title,
              id: event.id,
              value: event.id,
            })),
          ];
        })
        .catch((error) => {
          console.error("Error fetching events:", error);
        });
    },
    selectedEvents() {
      this.fetchReviews();
    },
    filterStatus() {
      this.fetchReviews();
    },
    clearDate() {
      this.rangeDate = null;
    },
    dateFormat(date) {
      if (!date) return "N/A";
      // Try to parse the date - backend might send in MM-DD-YYYY format
      const parsedDate = moment(
        String(date),
        ["MM-DD-YYYY", "M-D-YYYY", "YYYY-MM-DD"],
        true
      );
      if (parsedDate.isValid()) {
        return parsedDate.format("DD/MM/YYYY");
      }
      // Fallback to default parsing if specific format fails
      return moment(String(date)).format("DD/MM/YYYY");
    },
    onRowSelected(items) {
      if (items.length > 0) {
        this.$router.push({
          name: "review-preview",
          params: { id: items[0].id },
        });
      }
    },
    deleteReview(surveyId) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then((result) => {
        if (result.isConfirmed) {
          store
            .dispatch("review/deleteReview", surveyId)
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Success",
                  icon: "CheckIcon",
                  text: "Quality Review deleted successfully",
                  variant: "success",
                },
              });
              this.fetchReviews();
            })
            .catch((error) => {
              console.error("Error deleting review:", error);
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Error",
                  icon: "AlertTriangleIcon",
                  text: "Error deleting review",
                  variant: "danger",
                },
              });
            });
        }
      });
    },
  },
  watch: {
    filter() {
      this.fetchReviews();
    },
    rangeDate() {
      if (this.rangeDate === null) {
        this.fetchReviews();
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<style lang="scss" scoped>
.table-responsive {
  overflow-x: auto;
}
</style>
